import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  Select,
  ToggleSwitch,
} from "@primer/react";
import { useForm } from "react-hook-form";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { useSelector } from "react-redux";
import { thangDiemApi } from "../../api/thangDiemApi";
import { monThiThanhPhanApi } from "../../api/monThiThanhPhanApi";
import { ex_monthi_thanhphan } from "../../models/response/monthi_thanhphan/ex_monthi_thanhphan";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface IProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
  ex_monthi_id: number;
}

interface IThangDiem {
  id: number;
  thang_diem: string;
}

interface IApiResponse<T> {
  is_success: boolean;
  data: T;
  message?: string;
}

interface IThangDiemResponse {
  id: number;
  thang_diem: string;
  dm_coso_id: number;
}

const MonThiThanhPhanDetailModal: React.FC<IProps> = ({
  id,
  onSuccess,
  onCancel,
  ex_monthi_id,
}) => {
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();
  const [thangDiems, setThangDiems] = useState<IThangDiem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<ex_monthi_thanhphan>({
      defaultValues: {
        id: id,
        diem_thanh_phan: "",
        diem_thanh_phan_en: "",
        nhan_xet_rieng: false,
        trong_so: 0,
        ex_thangdiem_id: 0,
        ex_monthi_id: ex_monthi_id,
      },
    });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (id) {
      try {
        const res = (await monThiThanhPhanApi.detail(
          id
        )) as IApiResponse<ex_monthi_thanhphan>;
        if (res.is_success && res.data) {
          reset(res.data);
        }
      } catch {
        NotifyHelper.Error("Có lỗi khi tải chi tiết");
      }
    }

    try {
      const res = (await thangDiemApi.selectAll()) as IApiResponse<
        IThangDiemResponse[]
      >;
      if (res.is_success) {
        const filtered = res.data.filter(
          (x: IThangDiemResponse) =>
            x &&
            x.dm_coso_id === dm_coso_id &&
            x.id &&
            x.thang_diem &&
            x.thang_diem.trim() !== ""
        );
        setThangDiems(filtered);
      }
    } catch {
      NotifyHelper.Error("Có lỗi khi tải thang điểm");
    }
  };

  const onSubmit = async (data: ex_monthi_thanhphan) => {
    setIsSaving(true);
    try {
      const api = id ? monThiThanhPhanApi.update : monThiThanhPhanApi.insert;
      const res = await api({
        ...data,
        ex_monthi_id,
      });

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id ? "Cập nhật thành công" : "Thêm mới thành công")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || "Có lỗi xảy ra");
      }
    } catch {
      NotifyHelper.Error("Có lỗi xảy ra");
    }
    setIsSaving(false);
  };

  return (
    <Dialog
      title={translate(id ? "Base.Label.Update" : "Base.Label.Create")}
      onClose={onCancel}
      width="medium"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Điểm thành phần")}</FormControl.Label>
          <TextInput {...register("diem_thanh_phan")} block />
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>
            {translate("Điểm thành phần (English)")}
          </FormControl.Label>
          <TextInput {...register("diem_thanh_phan_en")} block />
        </FormControl>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 3,
            mb: 3,
            alignItems: "center",
          }}
        >
          <FormControl>
            <FormControl.Label>{translate("Trọng số")}</FormControl.Label>
            <TextInput {...register("trong_so")} type="number" block />
          </FormControl>

          <FormControl sx={{ pt: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="checkbox"
                id="nhan-xet-rieng-checkbox"
                checked={watch("nhan_xet_rieng")}
                onChange={(e) => setValue("nhan_xet_rieng", e.target.checked)}
              />
              <FormControl.Label
                htmlFor="nhan-xet-rieng-checkbox"
                sx={{ mb: 0 }}
              >
                {translate("Nhận xét riêng")}
              </FormControl.Label>
            </Box>
          </FormControl>
        </Box>

        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Thang điểm")}</FormControl.Label>
          <Select {...register("ex_thangdiem_id")} block>
            <Select.Option value="">Chọn thang điểm</Select.Option>
            {thangDiems.map((item) => (
              <Select.Option key={item.id} value={item.id.toString()}>
                {item.thang_diem}
              </Select.Option>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {translate(id ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MonThiThanhPhanDetailModal;
